import { Box, Typography, useTheme } from "@mui/material";
import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useDeferredValue, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ConfirmationDialog,
  ExpirationDatePicker,
  SearchBar,
  SelectableList,
  SelectableListItem,
} from "../../../components";
import { ASSIGNED_VIA_DIRECT } from "../../../components/AssignedViaCell";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import { LICENSE_ALLOCATIONS_QUERY_KEY } from "../../../hooks/useExternalExperiences";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { USER_RELEASE_CHANNELS_QUERY_KEY } from "../../users/hooks/useUserExperiences";
import { useUsers } from "../../users/hooks/useUsers";
import {
  INCOMING_LICENSES_QUERY_KEY,
  useIncomingLicenses,
} from "../hooks/useIncomingLicenses";
import {
  USERS_FOR_RELEASE_CHANNEL_QUERY_KEY,
  useUsersForReleaseChannel,
} from "../hooks/useUsersForReleaseChannel";

export interface GrantAccessToUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  openSuccessModal: () => void;
  releaseChannelId?: string;
  isExternal?: boolean;
  licenseId?: string;
  maxDate?: Date | null;
}

export const GrantAccessToUserDialog: FC<GrantAccessToUserDialogProps> = ({
  isOpen,
  onClose,
  openSuccessModal,
  releaseChannelId,
  isExternal = false,
  licenseId,
  maxDate,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [datePickerChecked, setDatePickerChecked] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const userManagementService = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();
  const queryClient = useQueryClient();
  const { companyId } = useParams();
  const { licensesByReleaseChannel } = useIncomingLicenses();

  const license = useMemo(() => {
    if (!isExternal || !releaseChannelId) return null;

    return licensesByReleaseChannel.get(releaseChannelId) ?? null;
  }, [isExternal, releaseChannelId, licensesByReleaseChannel]);

  const availableSeats = useMemo(() => {
    if (!license?.seats) return null;

    return license.seats - license.allocatedSeats;
  }, [license]);

  const hasWhitelist = useMemo(() => license?.hasWhitelist ?? false, [license]);
  const { users: allUsers, isLoading } = useUsers({
    companyId,
    // pass licenseId only if there is a whitelist to filter users accordingly, otherwise it will return no users
    licenseId: hasWhitelist ? licenseId : undefined,
  });

  const { usersAccess: existingUserAccess, isLoading: isLoadingAccess } =
    useUsersForReleaseChannel(releaseChannelId, {
      enabled: isOpen,
      isExternal,
    });

  const mappedItems = useMemo(() => {
    return allUsers.map<SelectableListItem>((user) => {
      const access = existingUserAccess[user.userId];
      const hasDirectAccess = access?.some((a) => a === ASSIGNED_VIA_DIRECT);
      const hasGroupAccess = access?.some((a) => a !== ASSIGNED_VIA_DIRECT);

      let tertiaryText: string | undefined;
      let type: "disabled" | "hasAccess" | "selectable" = "selectable";

      if (hasDirectAccess && hasGroupAccess) {
        tertiaryText = t("experiences.grantAccessToUser.directAndGroupAccess");
        type = "hasAccess";
      } else if (hasDirectAccess) {
        tertiaryText = t("experiences.grantAccessToUser.directAccess");
        type = "hasAccess";
      } else if (hasGroupAccess) {
        tertiaryText = t("experiences.grantAccessToUser.groupAccess");
      }

      if (
        type === "selectable" &&
        isExternal &&
        availableSeats !== null &&
        selectedUserIds.length >= availableSeats &&
        !selectedUserIds.includes(user.userId)
      ) {
        type = "disabled";
      }

      return {
        id: user.userId,
        primaryText: `${user.surname}, ${user.forename}`,
        secondaryText: user.emailAddress,
        tertiaryText,
        type,
      };
    });
  }, [
    allUsers,
    existingUserAccess,
    isExternal,
    availableSeats,
    selectedUserIds,
  ]);

  const filteredItems = useMemo(() => {
    if (deferredSearchTerm.trim()) {
      return mappedItems.filter((item) =>
        `${item.primaryText} ${item.secondaryText}`
          .toLowerCase()
          .includes(deferredSearchTerm.toLowerCase()),
      );
    }

    return mappedItems;
  }, [deferredSearchTerm, mappedItems]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
  };

  const handleSearchClear = () => {
    setSearchTerm("");
  };

  const handleUserSelect = useCallback(
    (userIdToSelect: string) => {
      setSelectedUserIds((prev) => {
        if (prev.includes(userIdToSelect)) {
          return prev.filter((id) => id !== userIdToSelect);
        } else {
          if (
            isExternal &&
            availableSeats !== null &&
            prev.length >= availableSeats
          ) {
            return prev;
          }

          return [...prev, userIdToSelect];
        }
      });
    },
    [isExternal, availableSeats],
  );

  const handleRemoveUser = useCallback((userIdToRemove: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userIdToRemove));
  }, []);

  const handleSubmit = async () => {
    if (selectedUserIds.length > 0 && (expirationDate || datePickerChecked)) {
      setIsSubmitLoading(true);
      const mutation = isExternal ? externalMutation : addUsersAccessMutation;
      try {
        await mutation.mutateAsync({
          userIds: selectedUserIds,
          expiresAt: datePickerChecked
            ? undefined
            : (expirationDate ?? undefined),
        });
      } catch {
        // Error handling managed by React Query / UI feedback
      } finally {
        setIsSubmitLoading(false);
      }
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedUserIds([]);
    setSearchTerm("");
    setExpirationDate(null);
    setDatePickerChecked(true);
  };

  const addUsersAccessMutation = useMutation({
    mutationFn: async ({
      userIds,
      expiresAt,
    }: {
      userIds: string[];
      expiresAt?: Date;
    }) => {
      const releaseChannel = new UserManagement_V1.ReleaseChannel({
        releaseChannelId: releaseChannelId!,
        requirements: UserManagement_V1.ReleaseChannelRequirements.Optional,
        userGroupId: undefined,
        expiresAt,
      });

      await Promise.all(
        userIds.map(async (userId) => {
          try {
            await userManagementService.addReleaseChannelToUser(
              userId,
              releaseChannel,
            );
          } catch (error: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (error?.status === 409 || error?.response?.status === 409) {
              return;
            }
            throw error;
          }
        }),
      );
    },
    onMutate: async ({ userIds, expiresAt }) => {
      const queryKey = [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId];
      await queryClient.cancelQueries({ queryKey });

      const previousUsers =
        queryClient.getQueryData<UserManagement_V1.UserAccessData[]>(queryKey);

      if (previousUsers) {
        queryClient.setQueryData<UserManagement_V1.UserAccessData[]>(
          queryKey,
          (oldData) => {
            if (!oldData) return oldData;

            const newEntries = userIds.map(
              (userId) =>
                new UserManagement_V1.UserAccessData({
                  userId,
                  userGroupId: undefined,
                  expiresAt: expiresAt ?? maxDate ?? undefined,
                }),
            );

            return [...oldData, ...newEntries];
          },
        );
      }

      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
          context.previousUsers,
        );
      }
    },
    onSettled: (_, __, { userIds }) => {
      void queryClient.invalidateQueries({
        queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });

      userIds.forEach((userId) => {
        void queryClient.invalidateQueries({
          queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY, userId],
        });
      });
    },
    onSuccess: () => {
      handleClose();
    },
  });

  const externalMutation = useMutation({
    mutationFn: async ({
      userIds,
      expiresAt,
    }: {
      userIds: string[];
      expiresAt?: Date;
    }) => {
      try {
        const allocations = userIds.map(
          (userId) =>
            new Licensing_V1.LicenseUserAllocationWithoutId({
              userId,
              expiresAt: expiresAt ?? undefined,
              isPinnedToLicenseExpiration: !expiresAt,
              isRevoked: false,
            }),
        );
        await licensingService.allocateLicenseUserSeats(
          licenseId!,
          allocations,
        );
      } catch (error: any) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error?.status === 409 || error?.response?.status === 409) {
          return;
        }
        throw error;
      }
    },

    onMutate: async ({ userIds, expiresAt }) => {
      const queryKey = [LICENSE_ALLOCATIONS_QUERY_KEY, licenseId];
      await queryClient.cancelQueries({ queryKey });

      const previousAllocations =
        queryClient.getQueryData<Licensing_V1.LicenseUserAllocation[]>(
          queryKey,
        );

      if (previousAllocations) {
        queryClient.setQueryData<Licensing_V1.LicenseUserAllocation[]>(
          queryKey,
          (oldData) => {
            if (!oldData) return oldData;

            const newEntries = userIds.map(
              (userId) =>
                new Licensing_V1.LicenseUserAllocation({
                  userId,
                  licenseId: licenseId!,
                  licenseUserAllocationId: `temp-${userId}`,
                  expiresAt: expiresAt ?? maxDate ?? undefined,
                  isPinnedToLicenseExpiration: !expiresAt,
                  isRevoked: false,
                }),
            );

            return [...oldData, ...newEntries];
          },
        );
      }

      const incomingLicensesQueryKey = [INCOMING_LICENSES_QUERY_KEY];
      await queryClient.cancelQueries({ queryKey: incomingLicensesQueryKey });

      const previousIncomingLicenses = queryClient.getQueriesData<
        Licensing_V1.License[]
      >({
        queryKey: incomingLicensesQueryKey,
      });

      queryClient.setQueriesData<Licensing_V1.License[]>(
        { queryKey: incomingLicensesQueryKey },
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((l) =>
            l.licenseId === licenseId
              ? ({
                  ...l,
                  allocatedSeats: l.allocatedSeats + userIds.length,
                } as Licensing_V1.License)
              : l,
          );
        },
      );

      return { previousAllocations, previousIncomingLicenses };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousAllocations) {
        queryClient.setQueryData(
          [LICENSE_ALLOCATIONS_QUERY_KEY, licenseId],
          context.previousAllocations,
        );
      }

      if (context?.previousIncomingLicenses) {
        for (const [queryKey, data] of context.previousIncomingLicenses) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
    },
    onSuccess: () => {
      handleClose();
      if (isExternal && availableSeats !== null) {
        openSuccessModal();
      }
    },
  });

  const actionText = useMemo(() => {
    if (isSubmitLoading) return t("experiences.grantAccessToUser.granting");
    if (isMobile) return t("experiences.grantAccessToUser.grant");
    if (selectedUserIds.length === 0)
      return t("experiences.grantAccessToUser.grantAccess");
    if (selectedUserIds.length === 1)
      return t("experiences.grantAccessToUser.grantAccessToOneUser");

    return t("experiences.grantAccessToUser.grantAccessToMultipleUsers", {
      count: selectedUserIds.length,
    });
  }, [isMobile, isSubmitLoading, selectedUserIds.length, t]);

  const selectedItems = useMemo(() => {
    return mappedItems.filter((item) => selectedUserIds.includes(item.id));
  }, [mappedItems, selectedUserIds]);

  const selectedItemsTitle = useMemo(() => {
    if (isExternal && availableSeats !== null) {
      const remaining = availableSeats - selectedUserIds.length;

      return (
        <Typography fontSize={12}>
          {t("experiences.grantAccessToUser.selectedUsers")}{" "}
          <span
            style={{ color: remaining === 0 ? theme.colors.red : "inherit" }}
          >
            ({t("experiences.grantAccessToUser.remainingSeats")}: {remaining})
          </span>
        </Typography>
      );
    }

    return t("experiences.grantAccessToUser.selectedUsers");
  }, [isExternal, availableSeats, selectedUserIds.length, theme.colors.red, t]);

  return (
    <ConfirmationDialog.Root
      aria-describedby="grant-access-to-user-description"
      aria-labelledby="grant-access-to-user-title"
      fullScreenOnMobile={true}
      fullWidth
      height="728px"
      maxWidth="xs"
      onClose={handleClose}
      open={isOpen}
    >
      <ConfirmationDialog.Title
        id="grant-access-to-user-title"
        showIcon={false}
        sx={{ fontSize: { xs: 18, md: 24 } }}
      >
        {t("experiences.grantAccessToUser.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <SearchBar
            handleClear={handleSearchClear}
            handleSubmit={handleSearchSubmit}
            onChange={handleSearchChange}
            placeholder={t("experiences.grantAccessToUser.searchPlaceholder")}
            sx={{ maxWidth: undefined }}
            value={searchTerm}
          />
        </form>
        <SelectableList
          emptyMessage={t("experiences.grantAccessToUser.noUsersAvailable")}
          height="100%"
          isLoading={isLoading || isLoadingAccess}
          items={filteredItems}
          maxHeight="100%"
          minHeight={0}
          multiSelect={true}
          onChipRemove={handleRemoveUser}
          onItemSelect={handleUserSelect}
          selectedIds={selectedUserIds}
          selectedItems={selectedItems}
          selectedItemsTitle={selectedItemsTitle}
          sx={{ flex: 1, minHeight: 0 }}
        />
        <Box
          sx={{
            visibility: selectedUserIds.length > 0 ? "visible" : "hidden",
          }}
        >
          <ExpirationDatePicker
            checked={datePickerChecked}
            disabled={false}
            isExternal={isExternal}
            key={selectedUserIds.length > 0 ? "active" : "inactive"}
            maxDate={maxDate ?? undefined}
            onChange={setExpirationDate}
            setChecked={setDatePickerChecked}
            value={expirationDate}
          />
        </Box>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleClose}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("experiences.grantAccessToUser.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={
            selectedUserIds.length === 0 ||
            (!datePickerChecked && expirationDate === null)
          }
          isLoading={isSubmitLoading}
          loadingText={t("experiences.grantAccessToUser.granting")}
          onClick={() => void handleSubmit()}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
